import { useState, useEffect } from 'react';
import { DollarSign, CreditCard, Smartphone, AlertCircle, CheckCircle2, ArrowRight, Wallet } from 'lucide-react';
import './PayoutSection.css';

export default function PayoutSection({ paymentMethods, onPayout }) {
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [amount, setAmount] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const totalEarned = 132.45;
  const availableAmount = totalEarned;
  const maxAmount = Math.min(availableAmount, selectedMethod?.maxAmount || availableAmount);

  useEffect(() => {
    if (paymentMethods.length > 0) {
      const defaultMethod = paymentMethods.find(method => method.isDefault);
      setSelectedMethod(defaultMethod || paymentMethods[0]);
    }
  }, [paymentMethods]);

  const handleAmountChange = (value) => {
    const numValue = parseFloat(value) || 0;
    if (numValue <= maxAmount) {
      setAmount(value);
    }
  };

  const handlePayout = async () => {
    if (!selectedMethod || !amount || parseFloat(amount) <= 0) return;
    
    setIsProcessing(true);
    await new Promise(resolve => setTimeout(resolve, 2000));
    setIsProcessing(false);
    setShowSuccess(true);
    
    setTimeout(() => {
      setShowSuccess(false);
      setAmount('');
      if (onPayout) {
        onPayout({ method: selectedMethod, amount: parseFloat(amount), timestamp: new Date() });
      }
    }, 3000);
  };

  const getMethodIcon = (method) => {
    return method.type === 'Mobile Money' ? 
      <Smartphone size={20} className="text-emerald-600" /> : 
      <CreditCard size={20} className="text-emerald-600" />;
  };

  const canPayout = selectedMethod && amount && parseFloat(amount) > 0 && parseFloat(amount) <= maxAmount;

  if (paymentMethods.length === 0) {
    return (
      <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm lg:p-6">
        <div className="py-6 text-center lg:py-8">
          <Wallet className="mx-auto mb-3 w-6 h-6 text-gray-400 lg:mb-4 lg:w-8 lg:h-8" />
          <h3 className="mb-2 text-base font-semibold text-gray-900 lg:text-lg">No Payment Methods</h3>
          <p className="text-xs text-gray-600 lg:text-sm">Add a payment method to start withdrawing</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm lg:p-6">
      <div className="mb-4 lg:mb-6">
        <h2 className="mb-2 text-lg font-semibold text-gray-900 lg:text-xl">Withdraw Commission</h2>
        <p className="text-xs text-gray-600 lg:text-sm">Select payment method and enter amount to withdraw</p>
      </div>

      {/* Available Balance */}
      <div className="p-3 mb-4 bg-gradient-to-r from-emerald-50 to-blue-50 rounded-xl border border-emerald-200 lg:p-4 lg:mb-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
          <div className="flex gap-3 items-center">
            <div className="flex justify-center items-center w-10 h-10 bg-emerald-100 rounded-full lg:w-12 lg:h-12">
              <DollarSign size={20} className="text-emerald-600 lg:w-6 lg:h-6" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-700 lg:text-sm">Available Balance</p>
              <p className="text-xl font-bold text-emerald-600 lg:text-2xl">${availableAmount.toFixed(2)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Method Selection */}
      <div className="mb-4 lg:mb-6">
        <label className="block mb-3 text-xs font-medium text-gray-700 lg:text-sm">Payment Method</label>
        <div className="grid gap-2 lg:gap-3">
          {paymentMethods.map((method) => (
            <div
              key={method.id}
              onClick={() => setSelectedMethod(method)}
              className={`relative p-3 lg:p-4 rounded-xl border-2 cursor-pointer payment-method-card ${
                selectedMethod?.id === method.id
                  ? 'border-emerald-500 bg-emerald-50 shadow-md scale-[1.02] selected'
                  : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
              }`}
            >
              <div className="flex gap-3 items-start">
                <div className="flex flex-1 gap-2 items-center min-w-0 lg:gap-3">
                  <div className="flex flex-shrink-0 justify-center items-center w-8 h-8 bg-emerald-100 rounded-full lg:w-10 lg:h-10">
                    {getMethodIcon(method)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate lg:text-base">{method.walletName || method.provider}</p>
                    <p className="text-xs text-gray-500 truncate lg:text-sm">{method.number}</p>
                  </div>
                </div>
                
                <div className="flex flex-shrink-0 gap-2 items-center">
                  {method.isDefault && (
                    <span className="px-2 py-1 text-xs font-medium text-emerald-700 whitespace-nowrap bg-emerald-100 rounded-full">
                      Default
                    </span>
                  )}
                  {selectedMethod?.id === method.id && (
                    <div className="flex justify-center items-center w-5 h-5 bg-emerald-500 rounded-full lg:w-6 lg:h-6">
                      <CheckCircle2 size={14} className="text-white lg:w-4 lg:h-4" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Amount Input */}
      <div className="mb-4 lg:mb-6">
        <label className="block mb-3 text-xs font-medium text-gray-700 lg:text-sm">Withdrawal Amount</label>
        <div className="relative">
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 lg:left-4">
            <DollarSign size={18} className="text-gray-400 lg:w-5 lg:h-5" />
          </div>
          <input
            type="number"
            value={amount}
            onChange={(e) => handleAmountChange(e.target.value)}
            placeholder="0.00"
            min="0"
            max={maxAmount}
            step="0.01"
            className="py-3 pr-4 pl-10 w-full text-base font-medium text-gray-900 bg-gray-50 rounded-xl border-2 border-gray-200 transition-all duration-200 lg:py-4 lg:pl-12 lg:text-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 amount-input"
          />
        </div>
        
        <div className="flex flex-col gap-2 mt-3 sm:flex-row sm:justify-between sm:items-center">
          <div className="flex gap-2 items-center">
            {amount && parseFloat(amount) > maxAmount && (
              <div className="flex gap-1 items-center text-red-600">
                <AlertCircle size={12} className="lg:w-3.5 lg:h-3.5" />
                <span className="text-xs">Amount exceeds available balance</span>
              </div>
            )}
          </div>
          <p className="text-xs text-gray-500">Max: ${maxAmount.toFixed(2)}</p>
        </div>
      </div>

      {/* Quick Amount Buttons */}
      <div className="mb-4 lg:mb-6">
        <p className="mb-3 text-xs font-medium text-gray-700 lg:text-sm">Quick Amounts</p>
        <div className="grid grid-cols-3 gap-2 sm:flex sm:flex-wrap">
          {[10, 25, 50, 75, 100].map((quickAmount) => (
            <button
              key={quickAmount}
              onClick={() => handleAmountChange(Math.min(quickAmount, maxAmount).toString())}
              disabled={quickAmount > maxAmount}
              className={`px-3 lg:px-4 py-2 text-xs lg:text-sm font-medium rounded-lg border quick-amount-btn ${
                quickAmount <= maxAmount
                  ? 'border-emerald-200 text-emerald-700 bg-emerald-50 hover:bg-emerald-100'
                  : 'border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed'
              }`}
            >
              ${quickAmount}
            </button>
          ))}
          <button
            onClick={() => handleAmountChange(maxAmount.toString())}
            className="px-3 py-2 text-xs font-medium text-emerald-700 bg-emerald-50 rounded-lg border border-emerald-200 lg:px-4 lg:text-sm hover:bg-emerald-100"
          >
            Max
          </button>
        </div>
      </div>

      {/* Payout Button */}
      <button
        onClick={handlePayout}
        disabled={!canPayout || isProcessing}
        className={`w-full py-3 lg:py-4 px-4 lg:px-6 rounded-xl font-semibold text-base lg:text-lg transition-all duration-300 ${
          canPayout && !isProcessing
            ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white hover:from-emerald-600 hover:to-emerald-700 shadow-lg'
            : 'bg-gray-200 text-gray-400 cursor-not-allowed'
        } ${isProcessing ? 'payout-button-loading' : ''}`}
      >
        {isProcessing ? (
          <div className="flex gap-2 justify-center items-center lg:gap-3">
            <div className="w-4 h-4 rounded-full border-2 border-white animate-spin lg:w-5 lg:h-5 border-t-transparent"></div>
            <span className="text-sm lg:text-base">Processing Payout...</span>
          </div>
        ) : (
          <div className="flex gap-2 justify-center items-center lg:gap-3">
            <span>Withdraw ${amount || '0.00'}</span>
            <ArrowRight size={18} className="lg:w-5 lg:h-5" />
          </div>
        )}
      </button>

      {/* Success Message */}
      {showSuccess && (
        <div className="p-3 mt-4 bg-emerald-50 rounded-xl border border-emerald-200 lg:p-4 success-message">
          <div className="flex gap-2 items-center lg:gap-3">
            <CheckCircle2 size={18} className="text-emerald-600 lg:w-5 lg:h-5" />
            <div>
              <p className="text-sm font-medium text-emerald-900 lg:text-base">Payout Successful!</p>
              <p className="text-xs text-emerald-700 lg:text-sm">
                ${amount} has been sent to {selectedMethod?.walletName || selectedMethod?.provider}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
